import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
// import { MoveUsdAppService } from '../../services/moveUsd/moveUsdAppService';
import axios from '../utils/axios';
import {
  RegisterIdentityDto,
  UpdateIdentityDto,
  CreateVirtualAccountDto,
  CreateRewardDto,
  RewardBalanceDto,
  CreateUsWireDepositDto,
  CreateCashDepositDto,
  ProductConfigurationDto,
  ProductFeeConfigurationDto,
  SearchProductConfigurationsDto,
  SearchProductFeeConfigurationsDto,
  DeleteConfigurationDto,
  MoveUsdResponseDto
} from '../models/moveUsd';

interface MoveUsdState {
  loading: boolean;
  error: string | null;
  productConfigurations: any[];
  productFeeConfigurations: any[];
  currentProduct: any | null;
  currentFee: any | null;
  identities: any[];
  virtualAccounts: any[];
  rewards: any[];
  rewardBalances: any[];
  transactions: any[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

const initialState: MoveUsdState = {
  loading: false,
  error: null,
  productConfigurations: [],
  productFeeConfigurations: [],
  currentProduct: null,
  currentFee: null,
  identities: [],
  virtualAccounts: [],
  rewards: [],
  rewardBalances: [],
  transactions: [],
  pagination: {
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 1
  }
};

// Helper function to handle common async thunk patterns
const createAsyncThunkHandler = <T>(thunk: any, serviceMethod: (arg: T) => Promise<MoveUsdResponseDto>) => {
  return createAsyncThunk(
    `moveUsd/${thunk}`,
    async (arg: T, { rejectWithValue }) => {
      try {
        const response = await serviceMethod(arg);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.message || 'An error occurred');
      }
    }
  );
};

// Identity Management
export const registerIdentity = createAsyncThunk(
  'moveUsd/registerIdentity',
  async (dto: RegisterIdentityDto, { rejectWithValue }) => {
    try {
      const response = await axios.post('api/services/app/MoveUsd/RegisterIdentity',{dto});
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to register identity');
    }
  }
);

export const updateIdentity = createAsyncThunk(
  'moveUsd/updateIdentity',
  async (dto: UpdateIdentityDto, { rejectWithValue }) => {
    try {
      const response = await axios.put('api/services/app/MoveUsd/UpdateIdentity',{dto});
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update identity');
    }
  }
);

// Virtual Accounts
export const createVirtualAccount = createAsyncThunk(
  'moveUsd/createVirtualAccount',
  async (dto: CreateVirtualAccountDto, { rejectWithValue }) => {
    try {
      const response = await axios.post('api/services/app/MoveUsd/CreateVirtualAccount',{dto});
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create virtual account');
    }
  }
);

// Rewards
export const createReward = createAsyncThunk(
  'moveUsd/createReward',
  async (dto: CreateRewardDto, { rejectWithValue }) => {
    try {
      const response = await axios.post('api/services/app/MoveUsd/CreateReward',{dto});
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create reward');
    }
  }
);

export const getRewardBalances = createAsyncThunk(
  'moveUsd/getRewardBalances',
  async (dto: RewardBalanceDto, { rejectWithValue }) => {
    try {
      const response = await axios.get(`api/services/app/MoveUsd/GetRewardBalances`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to get reward balances');
    }
  }
);

// Transactions
export const createUsWireDeposit = createAsyncThunk(
  'moveUsd/createUsWireDeposit',
  async (dto: CreateUsWireDepositDto, { rejectWithValue }) => {
    try {
      const response = await axios.post('api/services/app/MoveUsd/CreateUsWireDeposit',{dto});
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create US wire deposit');
    }
  }
);

export const createCashDeposit = createAsyncThunk(
  'moveUsd/createCashDeposit',
  async (dto: CreateCashDepositDto, { rejectWithValue }) => {
    try {
      const response = await axios.post('api/services/app/MoveUsd/CreateCashDeposit',{dto});
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create cash deposit');
    }
  }
);

// Product Configurations
export const createOrUpdateProductConfiguration = createAsyncThunk(
  'moveUsd/createOrUpdateProductConfiguration',
  async (dto: ProductConfigurationDto, { rejectWithValue }) => {
    try {
      const response = await axios.post('api/services/app/MoveUsd/CreateOrUpdateProductConfiguration',{dto});
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create/update product configuration');
    }
  }
);

export const searchProductConfigurations = createAsyncThunk(
  'moveUsd/searchProductConfigurations',
  async (dto: SearchProductConfigurationsDto, { rejectWithValue }) => {
    try {
      const response:any = await axios.post('api/services/app/MoveUsd/SearchProductConfigurations',{dto});
      return {
        data: response.data,
        pagination: {
          page: dto.page,
          pageSize: dto.pageSize,
          totalCount: response.totalCount || 0,
          totalPages: response.totalPages || 1
        }
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to search product configurations');
    }
  }
);

export const deleteProductConfiguration = createAsyncThunk(
  'moveUsd/deleteProductConfiguration',
  async (dto: DeleteConfigurationDto, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`api/services/app/MoveUsd/DeleteProductConfiguration?id=${dto.id}`);
      return { id: dto.id, ...response.data };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete product configuration');
    }
  }
);

// Product Fee Configurations
export const createOrUpdateProductFeeConfiguration = createAsyncThunk(
  'moveUsd/createOrUpdateProductFeeConfiguration',
  async (dto: ProductFeeConfigurationDto, { rejectWithValue }) => {
    try {
      const response = await axios.post('api/services/app/MoveUsd/CreateOrUpdateProductFeeConfiguration',{dto});
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create/update product fee configuration');
    }
  }
);

export const searchProductFeeConfigurations = createAsyncThunk(
  'moveUsd/searchProductFeeConfigurations',
  async (dto: SearchProductFeeConfigurationsDto, { rejectWithValue }) => {
    try {
      const response: any = await axios.post('api/services/app/MoveUsd/SearchProductFeeConfigurations',{dto});
      return {
        data: response.data,
        pagination: {
          page: dto.page,
          pageSize: dto.pageSize,
          totalCount: response.totalCount || 0,
          totalPages: response.totalPages || 1
        }
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to search product fee configurations');
    }
  }
);

export const deleteProductFeeConfiguration = createAsyncThunk(
  'moveUsd/deleteProductFeeConfiguration',
  async (dto: DeleteConfigurationDto, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`api/services/app/MoveUsd/DeleteProductFeeConfiguration?id=${dto.id}`);
      return { id: dto.id, ...response.data };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete product fee configuration');
    }
  }
);

const moveUsdSlice = createSlice({
  name: 'moveUsd',
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = null;
    },
    setCurrentProduct: (state, action: PayloadAction<any>) => {
      state.currentProduct = action.payload;
    },
    setCurrentFee: (state, action: PayloadAction<any>) => {
      state.currentFee = action.payload;
    },
    clearProductConfigurations: (state) => {
      state.productConfigurations = [];
    },
    clearProductFeeConfigurations: (state) => {
      state.productFeeConfigurations = [];
    }
  },
  extraReducers: (builder) => {
    

    // Identity Management
    builder.addCase(registerIdentity.fulfilled, (state, action) => {
      state.loading = false;
      state.identities.push(action.payload);
    });

    builder.addCase(updateIdentity.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.identities.findIndex(i => i.id === action.payload.id);
      if (index !== -1) {
        state.identities[index] = action.payload;
      }
    });

    // Virtual Accounts
    builder.addCase(createVirtualAccount.fulfilled, (state, action) => {
      state.loading = false;
      state.virtualAccounts.push(action.payload);
    });

    // Rewards
    builder.addCase(createReward.fulfilled, (state, action) => {
      state.loading = false;
      state.rewards.push(action.payload);
    });

    builder.addCase(getRewardBalances.fulfilled, (state, action) => {
      state.loading = false;
      state.rewardBalances = action.payload;
    });

    // Transactions
    builder.addCase(createUsWireDeposit.fulfilled, (state, action) => {
      state.loading = false;
      state.transactions.push(action.payload);
    });

    builder.addCase(createCashDeposit.fulfilled, (state, action) => {
      state.loading = false;
      state.transactions.push(action.payload);
    });

    // Product Configurations
    builder.addCase(createOrUpdateProductConfiguration.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.productConfigurations.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.productConfigurations[index] = action.payload;
      } else {
        state.productConfigurations.push(action.payload);
      }
      state.currentProduct = action.payload;
    });

    builder.addCase(searchProductConfigurations.fulfilled, (state, action) => {
      state.loading = false;
      state.productConfigurations = action.payload.data;
      state.pagination = {
        page: action.payload.pagination.page ?? 1,
        pageSize: action.payload.pagination.pageSize ?? 20,
        totalCount: action.payload.pagination.totalCount ?? 0,
        totalPages: action.payload.pagination.totalPages ?? 1
      };
    });

    builder.addCase(deleteProductConfiguration.fulfilled, (state, action) => {
      state.loading = false;
      state.productConfigurations = state.productConfigurations.filter(
        p => p.id !== action.payload.id
      );
    });

    // Product Fee Configurations
    builder.addCase(createOrUpdateProductFeeConfiguration.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.productFeeConfigurations.findIndex(f => f.id === action.payload.id);
      if (index !== -1) {
        state.productFeeConfigurations[index] = action.payload;
      } else {
        state.productFeeConfigurations.push(action.payload);
      }
      state.currentFee = action.payload;
    });

    builder.addCase(searchProductFeeConfigurations.fulfilled, (state, action) => {
      state.loading = false;
      state.productFeeConfigurations = action.payload.data;
      state.pagination = {
        page: action.payload.pagination.page ?? 1,
        pageSize: action.payload.pagination.pageSize ?? 20,
        totalCount: action.payload.pagination.totalCount ?? 0,
        totalPages: action.payload.pagination.totalPages ?? 1
      };
    });

    builder.addCase(deleteProductFeeConfiguration.fulfilled, (state, action) => {
      state.loading = false;
      state.productFeeConfigurations = state.productFeeConfigurations.filter(
        f => f.id !== action.payload.id
      );
    });

    // Common loading/error handling
    builder
      .addMatcher(
        (action) => action.type.startsWith('moveUsd/') && action.type.endsWith('/pending'),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.startsWith('moveUsd/') && action.type.endsWith('/rejected'),
        (state, action) => {
          state.loading = false;
          state.error = (action as any).payload as string;
        }
      );

  }
});

export const {
  resetError,
  setCurrentProduct,
  setCurrentFee,
  clearProductConfigurations,
  clearProductFeeConfigurations
} = moveUsdSlice.actions;

export default moveUsdSlice.reducer;